import { CogfyClient } from '@indigohive/cogfy-api-client'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { CookieManager } from 'react-cookie-manager'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import './i18n'
import {
  ApiKeysIndexPage,
  ChatMessagePage,
  ChatPage,
  CollectionPageV2,
  CollectionsIndexPage,
  CollectionTemplatePage,
  CollectionTemplatesPage,
  ConnectionsUsagesPage,
  CreateApiKeyPage,
  CreateProfilePage,
  CreateWebhookPage,
  CreditsUsagePage,
  FirstAccessPage,
  ForgotPasswordPage,
  RecordPage,
  ResetPasswordPage,
  RootPage,
  ScheduledMessagesPage,
  SignInPage,
  SignUpPage,
  TasksUsagePage,
  TotpSetupPage,
  TotpSignInPage,
  UserGroupsIndexPage,
  VerifyEmailPage,
  WebhooksIndexPage,
  WorkspaceAssistantPage,
  WorkspaceConnectionsPage,
  WorkspaceMembersPage,
  WorkspaceSettingsPage,
  WorkspaceUsagePage
} from './pages'
import { Layout } from './components/Layout'
import { AuthenticationProvider, AuthorizationProvider, ToastsProvider, DrawerProvider, PostHogPageViewTracker, FacebookSDKLoader, PostHogInitializer } from './components'
import { AppManagerProvider, CogfyClientProvider, OverlayProvider } from './contexts'
import { AppManager } from './lib'
import { PropsWithChildren } from 'react'
import { SettingsLayout } from './components/SettingsLayout/SettingsLayout'
import { OverviewUsagePage } from './pages/WorkspaceUsagePage/subpages/OverviewUsagePage'
import 'react-cookie-manager/style.css'

const cogfyClient = new CogfyClient({ baseURL: import.meta.env.VITE_COGFY_INTERNAL_API_URL })
const appManager = new AppManager({
  cogfyApiClient: cogfyClient,
  debug: import.meta.env.DEV
})

function Providers (props: PropsWithChildren) {
  return (
    <CookieManager
      translations={{
        title: 'Would You Like A Cookie? 🍪',
        message:
          'We value your privacy. Choose which cookies you want to allow. Essential cookies are always enabled as they are necessary for the website to function properly.',
        buttonText: 'Accept All',
        declineButtonText: 'Decline All',
        manageButtonText: 'Manage Cookies',
        privacyPolicyText: 'Privacy Policy'
      }}
      disableAutomaticBlocking={true}
      showManageButton={true}
      privacyPolicyUrl='https://lp.cogfy.com/privacy-policy'
      theme='light'
      displayType='popup'
    >
      <PostHogInitializer />
      <FacebookSDKLoader />
      <CogfyClientProvider client={cogfyClient}>
        <AuthenticationProvider>
          <AuthorizationProvider>
            <AppManagerProvider manager={appManager}>
              <OverlayProvider>
                <DrawerProvider>
                  <PostHogPageViewTracker />
                  {props.children}
                </DrawerProvider>
              </OverlayProvider>
            </AppManagerProvider>
          </AuthorizationProvider>
        </AuthenticationProvider>
      </CogfyClientProvider>
    </CookieManager>
  )
}

export function App () {
  return (
    <PostHogProvider client={posthog}>
      <DndProvider backend={HTML5Backend}>
        <ToastsProvider>
          <RouterProvider
            router={createBrowserRouter([
              {
                path: '/',
                element: (
                  <Providers>
                    <Outlet />
                  </Providers>
                ),
                children: [
                  {
                    path: ':workspace/*',
                    element: <Layout />,
                    children: [
                      { path: '', element: <CollectionsIndexPage /> },
                      { path: ':collectionId', element: <CollectionPageV2 /> },
                      { path: ':collectionId/:recordId', element: <RecordPage /> },
                      { path: 'chats/:chatId', element: <ChatPage /> },
                      { path: 'chats/:chatId/scheduled', element: <ScheduledMessagesPage /> },
                      { path: 'chat-messages/:chatMessageId', element: <ChatMessagePage /> },
                      {
                        path: '',
                        element: <SettingsLayout />,
                        children: [
                          { path: 'api-keys', element: <ApiKeysIndexPage /> },
                          { path: 'api-keys/create', element: <CreateApiKeyPage /> },
                          { path: 'connections', element: <WorkspaceConnectionsPage /> },
                          { path: 'groups', element: <UserGroupsIndexPage /> },
                          { path: 'assistant', element: <WorkspaceAssistantPage /> },
                          { path: 'members', element: <WorkspaceMembersPage /> },
                          { path: 'settings', element: <WorkspaceSettingsPage /> },
                          {
                            path: 'usage',
                            element: <WorkspaceUsagePage />,
                            children: [
                              { index: true, element: <Navigate to='overview' /> },
                              { path: 'overview', element: <OverviewUsagePage /> },
                              { path: 'tasks', element: <TasksUsagePage /> },
                              { path: 'credits', element: <CreditsUsagePage /> },
                              { path: 'connections', element: <ConnectionsUsagesPage /> }
                            ]
                          },
                          { path: 'webhooks', element: <WebhooksIndexPage /> },
                          { path: 'webhooks/create', element: <CreateWebhookPage /> }
                        ]
                      },
                      { path: 'templates', element: <CollectionTemplatesPage /> },
                      { path: 'templates/:templateSlug', element: <CollectionTemplatePage /> }
                    ]
                  },
                  { path: '/', element: <RootPage /> },
                  { path: '/create-profile', element: <CreateProfilePage /> },
                  { path: '/sign-in', element: <SignInPage /> },
                  { path: '/sign-up', element: <SignUpPage /> },
                  { path: '/first-access', element: <FirstAccessPage /> },
                  { path: '/forgot-password', element: <ForgotPasswordPage /> },
                  { path: '/reset-password', element: <ResetPasswordPage /> },
                  { path: '/totp-setup', element: <TotpSetupPage /> },
                  { path: '/totp-sign-in', element: <TotpSignInPage /> },
                  { path: '/verify-email', element: <VerifyEmailPage /> }
                ]
              }
            ])}
          />
        </ToastsProvider>
      </DndProvider>
    </PostHogProvider>
  )
}
